'use client';

import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader } from '@/components/ui/loader';
import { BarChart3, Pencil, Swords } from 'lucide-react';
import { apiGet } from '@/lib/api';
import type {
  MatchStatsResponse,
  TournamentMatch,
  TournamentTeam,
} from '../interfaces/tournaments';
import { MATCH_STATUS_LABELS } from '../interfaces/tournaments';
import { cn } from '@/lib/utils';

interface MatchStatsModalProps {
  match: TournamentMatch;
  teams: TournamentTeam[];
  orgId?: string;
  tournamentId?: string;
  slugOrId?: string;
  canEdit?: boolean;
  onEdit?: () => void;
  onClose: () => void;
}

export const MatchStatsModal: React.FC<MatchStatsModalProps> = ({
  match,
  teams,
  orgId,
  tournamentId,
  slugOrId,
  canEdit = false,
  onEdit,
  onClose,
}) => {
  const [stats, setStats] = useState<MatchStatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const path =
          orgId && tournamentId
            ? `/organizations/${orgId}/tournaments/${tournamentId}/matches/${match.id}/stats`
            : `/public/tournaments/${slugOrId}/matches/${match.id}/stats`;
        const data = await apiGet<MatchStatsResponse>(path);
        if (!cancelled) setStats(data);
      } catch (err: any) {
        if (!cancelled) setError(err.message || 'No se pudieron cargar las estadísticas');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [match.id, orgId, tournamentId, slugOrId]);

  const getTeamName = (teamId?: string | null) =>
    teams.find((team) => team.id === teamId)?.team_name ||
    stats?.teams.find((team) => team.id === teamId)?.team_name ||
    'TBD';

  const hasStats = Boolean(stats?.maps?.some((map) => map.teams.some((team) => team.players.length > 0)));

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-y-auto custom-scrollbar">
        <DialogHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <DialogTitle className="font-display text-xl flex items-center gap-2">
                <Swords className="h-5 w-5 text-primary" />
                Partida R{match.round} · M{match.match_number}
              </DialogTitle>
              <DialogDescription className="mt-1">
                {getTeamName(match.team1_id)} vs {getTeamName(match.team2_id)}
              </DialogDescription>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline">{MATCH_STATUS_LABELS[match.status]}</Badge>
              {canEdit && onEdit && (
                <Button variant="outline" size="sm" onClick={onEdit}>
                  <Pencil className="h-4 w-4 mr-2" />
                  Editar resultado
                </Button>
              )}
            </div>
          </div>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader />
          </div>
        ) : error ? (
          <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-6 text-center text-sm text-destructive">
            {error}
          </div>
        ) : !hasStats ? (
          <div className="rounded-xl border border-dashed border-border/60 bg-muted/10 p-10 text-center">
            <BarChart3 className="h-10 w-10 mx-auto text-muted-foreground/50 mb-3" />
            <p className="font-medium text-foreground">Sin estadísticas disponibles</p>
            <p className="text-sm text-muted-foreground mt-1">
              {match.status === 'completed'
                ? 'Esta partida no tiene stats importadas aún.'
                : 'Las estadísticas aparecerán cuando la partida se complete e importe el Match ID.'}
            </p>
            {canEdit && onEdit && (
              <Button variant="outline" className="mt-4" onClick={onEdit}>
                <Pencil className="h-4 w-4 mr-2" />
                Actualizar partida
              </Button>
            )}
          </div>
        ) : (
          <Tabs defaultValue={`map-${stats?.maps[0]?.map_number ?? 1}`}>
            <TabsList className="mb-4 flex-wrap h-auto">
              {stats?.maps.map((map) => (
                <TabsTrigger key={map.map_number} value={`map-${map.map_number}`}>
                  {map.map_name} ({map.team1_score}-{map.team2_score})
                </TabsTrigger>
              ))}
            </TabsList>

            {stats?.maps.map((map) => (
              <TabsContent key={map.map_number} value={`map-${map.map_number}`} className="space-y-4">
                <div className="grid gap-4 lg:grid-cols-2">
                  {map.teams.map((team) => (
                    <div
                      key={team.team_id}
                      className="rounded-xl border border-border/60 bg-card/40 overflow-hidden"
                    >
                      <div className="px-4 py-3 border-b border-border/50 bg-muted/20">
                        <h4 className="font-semibold text-foreground">{team.team_name}</h4>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-muted-foreground border-b border-border/40">
                              <th className="px-3 py-2 text-left">Jugador</th>
                              <th className="px-2 py-2 text-center">Agente</th>
                              <th className="px-2 py-2 text-center">K/D/A</th>
                              <th className="px-2 py-2 text-center">ACS</th>
                              <th className="px-2 py-2 text-center">DMG</th>
                              <th className="px-2 py-2 text-center">HS%</th>
                            </tr>
                          </thead>
                          <tbody>
                            {team.players.length === 0 ? (
                              <tr>
                                <td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">
                                  Sin datos
                                </td>
                              </tr>
                            ) : (
                              team.players
                                .sort((a, b) => b.acs - a.acs)
                                .map((player) => (
                                  <tr
                                    key={`${player.henrik_puuid}-${player.agent}`}
                                    className="border-b border-border/20 last:border-0"
                                  >
                                    <td className="px-3 py-2">
                                      <p className="font-medium text-foreground">
                                        {player.riot_name}#{player.riot_tag}
                                      </p>
                                    </td>
                                    <td className="px-2 py-2 text-center capitalize">{player.agent || '—'}</td>
                                    <td className="px-2 py-2 text-center font-mono">
                                      {player.kills}/{player.deaths}/{player.assists}
                                    </td>
                                    <td className="px-2 py-2 text-center font-semibold text-primary">
                                      {player.acs}
                                    </td>
                                    <td className="px-2 py-2 text-center">{player.damage_made}</td>
                                    <td className="px-2 py-2 text-center">{player.hs_pct}%</td>
                                  </tr>
                                ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        )}

        {match.team1_score != null && match.team2_score != null && (
          <div className="rounded-xl border border-primary/20 bg-primary/5 px-4 py-3 text-center">
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Resultado serie</p>
            <p className={cn('font-display text-2xl font-bold text-foreground')}>
              {match.team1_score} - {match.team2_score}
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MatchStatsModal;
